/* eslint-disable jsx-a11y/label-has-associated-control */
// TODO: Add 'relist' action
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { BrowserRouter, Route, RouteComponentProps } from 'react-router-dom';
import { isEqual, debounce } from 'lodash';
import ReactDOM from 'react-dom';
import axios from 'axios';
import Select from 'react-select';
import NumberFormat from 'react-number-format';
import {
  Formik,
  Field,
  Form,
  ErrorMessage,
} from 'formik';
import Swal from 'sweetalert2';
import { Button, Modal, Spinner } from 'react-bootstrap';
import routes from '../../../../utils/routes';
import { SellerSearchState, UserData, SearchResponse } from '../../admin-dashboard-tables/interfaces';
import {
  ADS_ADMIN_PREFIX,
  ALGOLIA_ALL_LISTINGS_INDEX,
  ALGOLIA_LITTERS_INDEX,
} from '../../../../utils/constants';
import AdminDashboardTableHits from '../../admin-dashboard-tables/AdminDashboardTableHits';
import useModal from '../../../../utils/hooks/useModal';
import ConfirmationModal from '../../widgets/ConfirmationModal';
import useBulkSelected from '../../../../utils/hooks/useBulkSelected';
import BulkSelectorMenu from '../../widgets/BulkSelectorMenu';
import StatusFilterDisplay from '../../widgets/filters/display-components/StatusFilterDisplay';
import { sellerBulkListingPriceChangeValidator } from '../../admin-dashboard-tables/validators';
import { SortByItem } from '../../widgets/sorts/SimpleSortBy';
import { useAlgoliaSearchClient } from '../../../../utils/hooks/useAlgoliaSearchClient';
import LitterHit from './LitterHit';
import AdHit from './AdHit';
import SellerAdsWidget from './SellerAdsWidget';
import AdditionalInfoTextArea from './AdditionalInfoTextArea';

const Hits: React.FC<{
  hits: any,
  noResultsMessage?: string,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
  HitComponent: React.FC<any>,
  selectedListings?: { id: number, listings_type: string }[],
  setSelectedListings?: (listings: Array<{ id: number, listings_type: string }>) => void,
  user: UserData
}> = ({
  hits,
  selected,
  toggleSelected,
  HitComponent,
  selectedListings,
  setSelectedListings,
  user,
}) => {
  return (
    <div className="profile-table">
      <AdminDashboardTableHits
        hits={hits}
        Hit={HitComponent}
        selected={selected}
        toggleSelected={toggleSelected}
        trackSelected={selectedListings}
        setTrackedSelected={setSelectedListings}
        userIsApprovedSeller={user.seller_status === 'approved'}
      />
    </div>
  );
};

// Session sorts temporarily retain the last used
// sort state for the "My Puppy Ads" and "Deleted Ads" tables
const sessionSorts = sessionStorage.getItem('singleUserAdsDashboardSort')
  ? JSON.parse(sessionStorage.getItem('singleUserAdsDashboardSort')!)
  : null;

// Available "Sort By" options for Seller My Ads listing tables. Since these are currently
// unchanging, I've left them outside the component
const listingSorts = [
  {
    label: 'Name',
    value: `${ALGOLIA_ALL_LISTINGS_INDEX}_name_az`,
  },
  {
    label: 'Expire First',
    value: `${ALGOLIA_ALL_LISTINGS_INDEX}_expires_at_asc`,
  },
  {
    label: 'Expire Last',
    value: `${ALGOLIA_ALL_LISTINGS_INDEX}_expires_at_desc`,
  },
];

const defaultSorts: CurrentSorts = {
  myPuppyAds: listingSorts[0],
  deletedAds: listingSorts[0],
};

interface SellerAdsDashboard extends RouteComponentProps {
  user: UserData,
}

// The currently active sorts. Currently, sorts only apply to the "My Puppy Ads"
// and "Deleted Ads" sections
interface CurrentSorts {
  myPuppyAds: SortByItem,
  deletedAds: SortByItem,
}

interface PriceUpdateFields {
  price?: number | null,
  sale_price?: number | null,
  deposit_price?: number | null,
}

const SingleUserAdsDashboard: React.FC<SellerAdsDashboard> = ({
  user,
}): ReactElement => {
  const initialPaginationValues = {
    puppy: {
      page: 0,
      hitsPerPage: 25,
    },
    stud: {
      page: 0,
      hitsPerPage: 25,
    },
    litter: {
      page: 0,
      hitsPerPage: 25,
    },
    sold: {
      page: 0,
      hitsPerPage: 25,
    },
    deleted: {
      page: 0,
      hitsPerPage: 25,
    },
  };

  const [query, setQuery] = useState('');
  const [attributesState, setAttributesState] = useState<{
    [key: string]: Array<{
      value: string[],
      label: string,
    }>
  }>({
    ad_type: [],
  });
  const [externalSearchState, setExternalSearchState] = useState<SellerSearchState>({
    configure: {
      facetFilters: [],
      numericFilters: user.id ? [`user_id = ${user.id}`] : [],
    },
    query,
  });
  const [selectedListings, setSelectedListings] = useState<
    { id: number, listings_type: string, flag_id?: number }[]
  >([]);
  const [loadingPriceForm, setLoadingPriceForm] = useState(false);
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [fieldsEnabled, setFieldsEnabled] = useState<string[]>([]);
  const [listingValues, setListingValues] = useState<PriceUpdateFields>({
    price: null,
    sale_price: null,
    deposit_price: null,
  });
  const [algoliaError, setAlgoliaError] = useState(false);
  const [pagination, setPagination] = useState(initialPaginationValues);
  const [searchResults, setSearchResults] = useState<SearchResponse<unknown>[]>([]);
  const [currentSorts, setCurrentSorts] = useState<CurrentSorts>(sessionSorts || defaultSorts);
  const {
    bulkAction,
    setBulkAction,
    updateBulkSelected,
    bulkSelected,
    setHits,
    clearBulkSelected,
  } = useBulkSelected();

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
  } = useModal();

  useEffect(() => {
    const { id } = user;
    const { configure } = externalSearchState;
    if (configure) {
      const { numericFilters } = configure;
      if (!isEqual(numericFilters, [`user_id = ${id}`])) {
        setExternalSearchState({
          ...externalSearchState,
          configure: {
            ...externalSearchState.configure,
            numericFilters: [`user_id = ${id}`],
          },
        });
      }
    }
  }, [user]);

  const setHitsPerPage = (hitsPerPage: number, key: string) => {
    setPagination((p) => ({
      ...p,
      [key]: {
        ...p[key],
        page: 0,
        hitsPerPage,
      },
    }));
  };

  const setPage = (page: number, key: string) => {
    setPagination((p) => ({
      ...p,
      [key]: {
        ...p[key],
        page,
      },
    }));
  };

  /**
   * Resets the page numbers but maintains the current hitsPerPage values
   */
  const resetPageNumbers = () => {
    setPagination((p) => ({
      puppy: {
        ...p.puppy,
        page: 0,
      },
      stud: {
        ...p.stud,
        page: 0,
      },
      litter: {
        ...p.litter,
        page: 0,
      },
      sold: {
        ...p.sold,
        page: 0,
      },
      deleted: {
        ...p.deleted,
        page: 0,
      },
    }));
  };
  const onReset = () => {
    setPagination(initialPaginationValues);
    setAttributesState({
      ad_type: [],
    });
    setQuery('');
    sessionStorage.removeItem('singleUserAdsDashboardSort');
    setCurrentSorts(defaultSorts);
  };

  const handleClosePriceForm = () => setShowPriceForm(false);
  const handleShowPriceForm = () => setShowPriceForm(true);

  /**
   * Updates the fields that are enabled and able to be edited via bulk action
   * @param field - The name of the field that is to be enabled
   * @param add - Whether the field is to be toggled on or off
   * @returns {void}
   */
  const updateFieldsEnabled = useCallback((field: string, add: boolean) => {
    const newFields: string[] = [];
    if (add) {
      fieldsEnabled.forEach((currentField) => newFields.push(currentField));
      if (!fieldsEnabled.includes(field)) {
        newFields.push(field);
      }
    } else {
      fieldsEnabled.forEach((currentField) => {
        if (currentField !== field) {
          newFields.push(currentField);
        }
      });
    }
    if (!isEqual(fieldsEnabled, newFields)) {
      setFieldsEnabled(newFields);
    }
  }, [fieldsEnabled, setFieldsEnabled]);

  const onConfirmSubmitPriceForm = useCallback(async (values) => {
    setListingValues(values);

    const listings = selectedListings.map((listing) => {
      const {
        id,
        listings_type,
      } = listing;
      return {
        id,
        listings_type,
      };
    });

    const fieldsToUpdate: PriceUpdateFields = { ...values };

    if (fieldsToUpdate?.price) {
      fieldsToUpdate.price *= 100;
    }

    if (fieldsToUpdate?.sale_price) {
      fieldsToUpdate.sale_price *= 100;
    }

    if (fieldsToUpdate?.deposit_price) {
      fieldsToUpdate.deposit_price *= 100;
    }

    Object.keys(fieldsToUpdate).forEach((field) => {
      if (!fieldsEnabled.includes(field)) {
        delete fieldsToUpdate[field];
      }
    });
    return new Promise((res, rej) => {
      axios.patch(
        routes.LISTINGS_ALL_BULK_UPDATE,
        {
          listings,
          fields: fieldsToUpdate,
        },
      )
        .then(({
          data,
          status,
        }) => {
          if ((status >= 200 && status <= 299)) {
            Swal.fire(
              'Success',
              'Price updated successfully!',
              'success',
            ).then(() => {
              setLoadingPriceForm(false);
              setListingValues({
                price: null,
                sale_price: null,
                deposit_price: null,
              });
              setFieldsEnabled([]);
              res(data?.message || 'Listings updated successfully!');
              handleClosePriceForm();
            });
          }
        })
        .catch((e) => {
          Swal.fire(
            'Error',
            e?.message || 'Something went wrong; check your address and connection, then try again.',
            'error',
          ).then(() => {
            setShowPriceForm(true);
            setLoadingPriceForm(false);
            rej(e);
          });
        });
    });
  }, [selectedListings, listingValues, fieldsEnabled]);

  const onSubmitPriceForm = useCallback((values) => {
    setLoadingPriceForm(true);
    setModalData({
      message: 'Would you like to update the following fields?',
      ModalForm: () => (
        <ul>
          {
            fieldsEnabled.map((field) => (
              <li
                key={field}
                className="text-capitalize"
              >
                {field.replaceAll('_', ' ')}
              </li>
            ))
          }
        </ul>
      ),
      onConfirmAsync: () => onConfirmSubmitPriceForm(values),
      onSuccess: () => {
        setModalData({
          message: '',
          onClose: () => {
          },
          isSimple: true,
        });
        setShowModal(false);
        window.location.reload();
      },
      onClose: () => {
        setLoadingPriceForm(false);
        setModalData({
          message: '',
          onClose: () => {
          },
          isSimple: true,
        });
        setShowModal(false);
      },
    });
    setShowModal(true);
  }, [fieldsEnabled, onConfirmSubmitPriceForm]);

  // TODO: Add refresh code after performing a bulk operation; verify/update messages/errors
  const performBulkAction = useCallback((action: string) => {
    if (bulkSelected.length < 1) {
      setModalData({
        message: 'No ads have been selected. Please, select one or more ads to bulk edit.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {
            },
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {
            },
            isSimple: true,
          });
        },
      });
    } else {
      switch (action) {
        case 'unpublish':
          setModalData({
            message: `Would you like to delete ${bulkSelected.length} ${bulkSelected.length === 1 ? 'ad' : 'ads'}?`,
            onConfirmAsync: (fields) => {
              const listings = selectedListings.map((listing) => {
                const {
                  id,
                  listings_type,
                } = listing;
                return {
                  id,
                  listings_type,
                };
              });
              return new Promise((res, rej) => {
                axios.patch(
                  routes.LISTINGS_ALL_BULK_UPDATE,
                  {
                    listings,
                    fields: {
                      ...fields,
                      status: 'unpublished',
                    },
                  },
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => {
              performBulkAction('');
              setShowModal(false);
              window.location.reload();
            },
          });
          break;
        case 'set_sold':
          setModalData({
            message: `Would you like to update the 'Sold Status' of ${bulkSelected.length} ${bulkSelected.length === 1 ? 'ad' : 'ads'}?`,
            ModalForm: ({ formState }) => {
              useEffect(() => {
                if (formState.availability !== 'sold') {
                  setModalFormState((prevState) => ({
                    ...prevState,
                    sold_through: '',
                    sold_through_additional_info: '',
                  }));
                }
              }, [formState.availability]);

              return (
                <div>
                  <Select
                    className="mt-1"
                    placeholder="Choose a status..."
                    onChange={(e) => {
                      setModalFormState((prevState) => ({
                        ...prevState,
                        availability: e?.value,
                      }));
                    }}
                    options={[
                      {
                        label: 'Available',
                        value: 'available',
                      },
                      {
                        label: 'Sold',
                        value: 'sold',
                      },
                      {
                        label: 'Pending',
                        value: 'pending',
                      },
                    ]}
                  />
                  {formState?.availability === 'sold' && (
                    <div>
                      <div className="my-1">How was puppy sold?</div>
                      <Select
                        placeholder="Choose an option..."
                        onChange={(e) => {
                          // eslint-disable-next-line max-len
                          setModalFormState((prevState) => ({
                            ...prevState,
                            sold_through: e?.value,
                          }));
                        }}
                        options={[
                          {
                            label: 'Lancaster Puppies',
                            value: 'lancaster_puppies',
                          },
                          {
                            label: 'Other Advertising Site',
                            value: 'other_site',
                          },
                          {
                            label: 'Word of Mouth',
                            value: 'word_of_mouth',
                          },
                          {
                            label: 'Other',
                            value: 'other',
                          },
                        ]}
                      />
                      <AdditionalInfoTextArea
                        formState={formState}
                        onChange={(e) => {
                          setModalFormState((prevState) => ({
                            ...prevState,
                            sold_through_additional_info: e.target.value,
                          }));
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              const listings = selectedListings.map((listing) => {
                const {
                  id,
                  listings_type,
                } = listing;
                return {
                  id,
                  listings_type,
                };
              });
              return new Promise((res, rej) => {
                axios.patch(
                  routes.LISTINGS_ALL_BULK_UPDATE,
                  {
                    listings,
                    fields,
                  },
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => {
              performBulkAction('');
              setModalFormState({});
              window.location.reload();
            },
          });
          break;
        case 'set_price':
          handleShowPriceForm();
          break;
        default:
          setModalData({
            message: '',
            onClose: () => {
            },
            isSimple: true,
          });
      }
    }
  }, [bulkSelected, modalFormState, selectedListings]);

  useEffect(() => {
    const { value } = bulkAction;
    if (value) {
      performBulkAction(value);
    }
  }, [bulkAction]);

  const { searchClient } = useAlgoliaSearchClient(user?.public_key);

  const debounceQueryAlgolia = useRef(
    debounce(
      (searchState, queryPagination, attributes, sorts: CurrentSorts) => {
        const extraFacetFilters: (string[])[] = [];
        Object.entries(attributes).forEach(([key, values]) => {
          if (Array.isArray(values)) {
            const facetsSelected = values.map((attribute) => `${key}:${attribute.value[0]}`);
            extraFacetFilters.push(facetsSelected);
          }
        });
        const queries = [
          // My Puppy Ads
          {
            indexName: sorts.myPuppyAds.value,
            query: searchState.query,
            params: {
              facetFilters: [
                'listings_type:puppy',
                ['status:published', 'status:pending_approval'],
                'availability:-sold',
                ...extraFacetFilters,
              ],
              numericFilters: searchState?.configure?.numericFilters || [],
              page: queryPagination.puppy.page,
              hitsPerPage: queryPagination.puppy.hitsPerPage,
              restrictSearchableAttributes: [
                'seller_name',
                'seller_business_name',
                'seller_phone',
                'seller_phone_alternate_formats',
              ],
            },
          },
          // My Stud Ads
          {
            indexName: ALGOLIA_ALL_LISTINGS_INDEX!,
            query: searchState.query,
            params: {
              facetFilters: [
                'listings_type:stud',
                ['status:published', 'status:pending_approval'],
                'availability:-sold',
                ...extraFacetFilters,
              ],
              numericFilters: searchState?.configure?.numericFilters || [],
              page: queryPagination.stud.page,
              hitsPerPage: queryPagination.stud.hitsPerPage,
            },
          },
          // My Litters
          {
            indexName: ALGOLIA_LITTERS_INDEX!,
            query: searchState.query,
            params: {
              facetFilters: searchState?.configure?.facetFilters || [],
              numericFilters: searchState?.configure?.numericFilters || [],
              page: queryPagination.litter.page,
              hitsPerPage: queryPagination.litter.hitsPerPage,
            },
          },
          // My Sold Ads
          {
            indexName: ALGOLIA_ALL_LISTINGS_INDEX!,
            query: searchState.query,
            params: {
              facetFilters: extraFacetFilters,
              numericFilters: [
                'sold_at > 0',
                ...(searchState?.configure?.numericFilters || []),
              ],
              page: queryPagination.sold.page,
              hitsPerPage: queryPagination.sold.hitsPerPage,
            },
          },
          // Deleted Ads
          {
            indexName: sorts.deletedAds.value,
            query: searchState.query,
            params: {
              facetFilters: [
                'status:unpublished',
                ...extraFacetFilters,
              ],
              numericFilters: searchState?.configure?.numericFilters || [],
              page: queryPagination.deleted.page,
              hitsPerPage: queryPagination.deleted.hitsPerPage,
            },
          },
        ];
        searchClient.multipleQueries(queries).then(({ results }) => {
          setAlgoliaError(false);

          // multipleQueries can also be used to perform facet queries, so that return type has now
          // been added to `results`. However, we do not use it for that, so we can expect our
          // `results` to only contain SearchResponse types
          // See: https://github.com/algolia/algoliasearch-client-javascript/pull/1460
          setSearchResults(results as SearchResponse<unknown>[]);
        }).catch((e) => {
          setAlgoliaError(true);
        });
      }, 300,
    ),
  ).current;
  useEffect(() => {
    return () => {
      debounceQueryAlgolia.cancel();
    };
  }, [debounceQueryAlgolia]);

  const searchAlgolia = useCallback(async () => {
    debounceQueryAlgolia(externalSearchState, pagination, attributesState, currentSorts);
  }, [externalSearchState, pagination, attributesState, debounceQueryAlgolia, currentSorts]);

  useEffect(() => {
    searchAlgolia();
  }, [searchAlgolia]);

  useEffect(() => {
    if (externalSearchState.query !== query) {
      setExternalSearchState((e) => ({
        ...e,
        query,
      }));
      // Reset pagination if query changes
      resetPageNumbers();
    }
  }, [query]);

  useEffect(() => {
    // Reset pagination if attributes change
    resetPageNumbers();
  }, [attributesState]);

  const setSort = useCallback((sort: SortByItem, key: string) => {
    setCurrentSorts((s) => {
      const newSorts = {
        ...s,
        [key]: sort,
      };
      sessionStorage.setItem('singleUserAdsDashboardSort', JSON.stringify(newSorts));
      return newSorts;
    });
  }, [setCurrentSorts]);

  return (
    <>
      {(!user || !user.id || algoliaError)
        ? (
          <div className="alert alert-danger" role="alert">
            <h2>Error</h2>
            <p>Could not load ads. Check your connection and try again.</p>
          </div>
        ) : (
          <div className="d-flex my-ads-page flex-wrap">
            <div className="left-side">
              <div className="ad-table-container table-type-puppy mb-4">
                <SellerAdsWidget
                  title="My Puppy Ads"
                  titleClass="text-muted"
                  tableWrapperClass="ads-table"
                  BulkSelector={(
                    <div className="ml-auto">
                      <BulkSelectorMenu
                        actions={[
                          {
                            label: 'Set Price',
                            value: 'set_price',
                          },
                          {
                            label: 'Set Availability Status',
                            value: 'set_sold',
                          },
                          {
                            label: 'Delete',
                            value: 'unpublish',
                          },
                        ]}
                        setBulkAction={setBulkAction}
                        bulkAction={bulkAction}
                      />
                    </div>
                  )}
                  bulkActions={[]}
                  query={externalSearchState.query}
                  customHits={(
                    <Hits
                      user={user}
                      hits={searchResults[0]?.hits || []}
                      HitComponent={AdHit}
                      selected={bulkSelected}
                      toggleSelected={updateBulkSelected}
                      selectedListings={selectedListings}
                      setSelectedListings={setSelectedListings}
                      noResultsMessage="You have no current puppy ads."
                    />
                  )}
                  searchResults={searchResults[0]}
                  setPage={(page: number) => setPage(page - 1, 'puppy')}
                  onChangeHitsPerPage={(hitsPerPage: number) => setHitsPerPage(hitsPerPage, 'puppy')}
                  attributes={[
                    {
                      type: 'facet',
                      attribute: 'ad_type',
                      label: 'Ad Type',
                      isSearchable: false,
                      isAsync: false,
                      isMulti: true,
                      DisplayComponent: <StatusFilterDisplay title="Ad Type:" />,
                      placeholder: '',
                      options: [
                        {
                          value: [
                            'basic',
                          ],
                          label: 'basic',
                        },
                        {
                          value: [
                            'premium',
                          ],
                          label: 'premium',
                        },
                        {
                          value: [
                            'advanced',
                          ],
                          label: 'advanced',
                        },
                      ],
                      selectedValue: attributesState.ad_type,
                      setSelectedValue: (value) => {
                        setAttributesState((a) => ({
                          ...a,
                          ad_type: value,
                        }));
                      },
                    },
                  ]}
                  setHits={setHits}
                  allowSetHitsPerPage={true}
                  onQueryUpdateCallback={(newQuery) => {
                    setQuery(newQuery);
                  }}
                  onReset={onReset}
                  sortByOptions={{
                    items: listingSorts,
                    currentSort: currentSorts.myPuppyAds,
                    setCurrentSort: setSort,
                    sortKey: 'myPuppyAds',
                  }}
                />
              </div>
            </div>
            <div className="right-side">
              <div className="ad-table-container table-type-stud mb-4">
                <SellerAdsWidget
                  title="My Stud Ads"
                  titleClass="text-muted mb-0"
                  tableWrapperClass="ads-table no-bottom-margin-on-header"
                  bulkActions={[]}
                  query={externalSearchState.query}
                  customHits={(
                    <Hits
                      user={user}
                      hits={searchResults[1]?.hits || []}
                      HitComponent={AdHit}
                      selected={bulkSelected}
                      toggleSelected={updateBulkSelected}
                      selectedListings={selectedListings}
                      setSelectedListings={setSelectedListings}
                      noResultsMessage="You have no current stud ads."
                    />
                  )}
                  searchResults={searchResults[1]}
                  setPage={(page: number) => setPage(page - 1, 'stud')}
                  onChangeHitsPerPage={(hitsPerPage: number) => setHitsPerPage(hitsPerPage, 'stud')}
                  attributes={[]}
                  setHits={setHits}
                  allowSetHitsPerPage={true}
                />
              </div>
              <div className="ad-table-container ad-table-container mb-4">
                <SellerAdsWidget
                  title="My Litters"
                  titleClass="text-muted mb-0"
                  tableWrapperClass="ads-table no-bottom-margin-on-header"
                  bulkActions={[]}
                  query={externalSearchState.query}
                  customHits={(
                    <Hits
                      user={user}
                      hits={searchResults[2]?.hits || []}
                      selected={[]}
                      toggleSelected={() => {
                      }}
                      HitComponent={LitterHit}
                      noResultsMessage="You currently have no litters."
                    />
                  )}
                  searchResults={searchResults[2]}
                  setPage={(page: number) => setPage(page - 1, 'litter')}
                  onChangeHitsPerPage={(hitsPerPage: number) => setHitsPerPage(hitsPerPage, 'litter')}
                  attributes={[]}
                  setHits={setHits}
                  allowSetHitsPerPage={true}
                />
              </div>
              <div className="ad-table-container ad-table-container mb-4">
                <SellerAdsWidget
                  title="My Sold Ads"
                  titleClass="text-muted mb-0"
                  tableWrapperClass="ads-table no-bottom-margin-on-header"
                  bulkActions={[]}
                  query={externalSearchState.query}
                  customHits={(
                    <Hits
                      user={user}
                      hits={searchResults[3]?.hits || []}
                      HitComponent={AdHit}
                      selected={bulkSelected}
                      toggleSelected={updateBulkSelected}
                      selectedListings={selectedListings}
                      setSelectedListings={setSelectedListings}
                      noResultsMessage="You Have No Sold Ads."
                    />
                  )}
                  searchResults={searchResults[3]}
                  setPage={(page: number) => setPage(page - 1, 'sold')}
                  onChangeHitsPerPage={(hitsPerPage: number) => setHitsPerPage(hitsPerPage, 'sold')}
                  attributes={[]}
                  setHits={setHits}
                  allowSetHitsPerPage={true}
                />
              </div>
              <div className="ad-table-container ad-table-container mb-4">
                <SellerAdsWidget
                  title="Deleted Ads"
                  titleClass="text-muted mb-0"
                  tableWrapperClass="ads-table no-bottom-margin-on-header"
                  bulkActions={[]}
                  query={externalSearchState.query}
                  customHits={(
                    <Hits
                      user={user}
                      hits={searchResults[4]?.hits || []}
                      HitComponent={AdHit}
                      selected={bulkSelected}
                      toggleSelected={updateBulkSelected}
                      selectedListings={selectedListings}
                      setSelectedListings={setSelectedListings}
                      noResultsMessage="You Have No Deleted Ads."
                    />
                  )}
                  searchResults={searchResults[4]}
                  setPage={(page: number) => setPage(page - 1, 'deleted')}
                  onChangeHitsPerPage={(hitsPerPage: number) => setHitsPerPage(hitsPerPage, 'deleted')}
                  attributes={[]}
                  setHits={setHits}
                  allowSetHitsPerPage={true}
                  sortByOptions={{
                    items: listingSorts,
                    currentSort: currentSorts.deletedAds,
                    setCurrentSort: setSort,
                    sortKey: 'deletedAds',
                  }}
                />
              </div>
            </div>
            <ConfirmationModal
              show={showModal}
              setShow={setShowModal}
              modalFormState={modalFormState}
              clearBulkSelected={clearBulkSelected}
              {...modalData}
            />
            <Modal
              show={showPriceForm}
              onHide={handleClosePriceForm}
              dialogClassName="modal-lg"
              backdropClassName="bg-blue-200"
              scrollable
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  {`Would you like to update the price of ${bulkSelected.length} ${bulkSelected.length === 1 ? 'ad' : 'ads'}?`}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div>
                  <Formik
                    enableReinitialize
                    initialValues={listingValues}
                    validationSchema={sellerBulkListingPriceChangeValidator}
                    onSubmit={async (values) => onSubmitPriceForm(values)}
                  >
                    {
                      (formikProps) => {
                        const {
                          errors,
                          touched,
                          dirty,
                          isValid,
                          values,
                          setFieldValue,
                          setFieldTouched,
                        } = formikProps;
                        return (
                          <Form className="my-ads-price-form">
                            <div className="row flex-column flex-md-row m-0">
                              <div className="col col-xs-2 col-md-2 mb-2">
                                <div className="toggle-slider-container d-flex align-items-center h-100">
                                  <label className="toggle-inner-container mb-0">
                                    <input
                                      id="toggle-price"
                                      checked={fieldsEnabled.includes('price')}
                                      onChange={() => {
                                        updateFieldsEnabled('price', !fieldsEnabled.includes('price'));
                                        setFieldValue('price', '');
                                        setFieldTouched('price', false);
                                      }}
                                      type="checkbox"
                                      aria-label="Enable Edit Price"
                                      className="toggle-slider-input"
                                    />
                                    <span className="toggle-slider" />
                                  </label>
                                </div>
                              </div>
                              <div className="col col-xs-12 col-md-10 col-lg-8 col-xl-6 mb-2">
                                <label
                                  htmlFor="price"
                                  className="font-weight-bold"
                                >
                                  Price
                                </label>
                                <div className="d-flex flex-column w-100">
                                  <Field name="price">
                                    {({ field }) => {
                                      const {
                                        onChange,
                                        ...rest
                                      } = field;
                                      return (
                                        <NumberFormat
                                          className={`bulk-number form-control${errors.price && touched.price ? ' is-invalid' : ''}`}
                                          aria-label="Price."
                                          id="price"
                                          thousandsGroupStyle="thousand"
                                          value={values.price}
                                          prefix="$"
                                          decimalSeparator="."
                                          displayType="input"
                                          type="text"
                                          thousandSeparator={true}
                                          allowNegative={false}
                                          decimalScale={2}
                                          disabled={!fieldsEnabled.includes('price')}
                                          placeholder="$1,000.00"
                                          onValueChange={({ floatValue }) => setFieldValue('price', floatValue)}
                                          {...rest}
                                        />
                                      );
                                    }}
                                  </Field>
                                  <ErrorMessage
                                    name="price"
                                    component="div"
                                    className="invalid-feedback"
                                  />
                                  {(fieldsEnabled.includes('price') && !values.price) && (
                                    <p
                                      id="price-note"
                                      className="text-danger"
                                    >
                                      If price is enabled, it must be set.
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="form-group d-flex justify-content-between mb-0">
                              <Button
                                variant="secondary"
                                disabled={loadingPriceForm}
                                onClick={handleClosePriceForm}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="submit"
                                className="btn btn-primary"
                                disabled={
                                  loadingPriceForm
                                  || !isValid
                                  || !dirty
                                  || (
                                    fieldsEnabled.includes('price')
                                    && !values.price
                                  )
                                  || fieldsEnabled.length === 0
                                }
                              >
                                {
                                  loadingPriceForm
                                    ? (
                                      <>
                                        <Spinner
                                          as="span"
                                          animation="border"
                                          size="sm"
                                          role="status"
                                          aria-hidden="true"
                                        />
                                        <span className="visually-hidden">
                                          Loading...
                                        </span>
                                      </>
                                    )
                                    : (<>Submit</>)
                                }
                              </Button>
                            </div>
                          </Form>
                        );
                      }
                    }
                  </Formik>
                </div>
              </Modal.Body>
            </Modal>
          </div>
        )}
    </>
  );
};

export default SingleUserAdsDashboard;

if (document.getElementById('react-seller-dashboard-table-ads')) {
  const element = document.getElementById('react-seller-dashboard-table-ads');
  const { user } = window;
  ReactDOM.render(
    <BrowserRouter>
      <Route
        path={`${ADS_ADMIN_PREFIX}`}
        component={(props) => <SingleUserAdsDashboard user={user} {...props} />}
      />
    </BrowserRouter>,
    element,
  );
}
